// src/components/Withdrawal.js - VERSION AMÉLIORÉE

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// 🎯 CONSTANTES - AJUSTÉES POUR TON BACKEND
const WALLET_OPTIONS = ['Binance', 'Trust Wallet', 'Coinbase', 'MetaMask'];
const NETWORK_VALUE = 'TRC20';
const NETWORK_NAME = `USDT (${NETWORK_VALUE})`;
const SERVICE_FEE_RATE = 0.05;
const NETWORK_FEE = 1.0; // 🔧 CORRECTION : 0.8 au lieu de 1.0 (selon ton backend)
const MIN_NET_AMOUNT = 0.01;

// 🔧 Configuration axios globale
const api = axios.create({
    baseURL: 'https://api.blackroq.pro/api',
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// 🔧 Interceptor pour ajouter le token automatiquement
api.interceptors.request.use(
    (config) => {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const Withdrawal = () => {
    const [formData, setFormData] = useState({
        amount: '',
        walletAddress: '',
        destinationWallet: ''
    });
    const [message, setMessage] = useState({ text: '', type: '' });
    const [loading, setLoading] = useState(false);
    const [userBalance, setUserBalance] = useState(null);
    const [loadingBalance, setLoadingBalance] = useState(true);
    
    const navigate = useNavigate();
    const { amount, walletAddress, destinationWallet } = formData;

    // 🔧 Récupérer le solde utilisateur
    useEffect(() => {
        const fetchUserBalance = async () => {
            try {
                setLoadingBalance(true);
                const response = await api.get('/auth/me');
                if (response.data) {
                    setUserBalance(response.data.withdrawableFunds || 0);
                }
            } catch (error) {
                console.error('Erreur récupération solde:', error);
            } finally {
                setLoadingBalance(false);
            }
        };
        
        fetchUserBalance();
    }, []);

    // 🔥 PARSING ROBUSTE
    const parseAmount = (val) => {
        if (!val) return 0;
        const sanitized = val.toString().replace(',', '.').replace(/[^0-9.]/g, '');
        const parts = sanitized.split('.');
        if (parts.length > 2) return parseFloat(parts[0]) || 0;
        return parseFloat(sanitized) || 0;
    };

    const requestedAmount = parseAmount(amount);

    // 🔥 CALCUL
    const calculateFees = (reqAmount) => {
        const serviceFee = reqAmount * SERVICE_FEE_RATE;
        const totalFees = serviceFee + NETWORK_FEE;
        return {
            serviceFee,
            networkFee: NETWORK_FEE,
            totalFees,
            netAmount: Math.max(0, reqAmount - totalFees)
        };
    };

    const { serviceFee, networkFee, totalFees, netAmount } = calculateFees(requestedAmount);

    const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

    const onSubmit = async e => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: '', type: '' });

        // VALIDATIONS
        if (requestedAmount <= 0) {
            setMessage({ 
                text: 'Le montant doit être supérieur à zéro.', 
                type: 'danger' 
            });
            setLoading(false);
            return;
        }
        
        // 🔧 Vérification solde
        if (userBalance !== null && requestedAmount > userBalance) {
            setMessage({ 
                text: `Solde insuffisant. Votre solde disponible : ${userBalance.toFixed(2)} USDT.`,
                type: 'danger' 
            });
            setLoading(false);
            return;
        }
        
        if (requestedAmount <= totalFees) {
            setMessage({ 
                text: `Le montant doit être supérieur aux frais (${totalFees.toFixed(2)} USDT).`,
                type: 'danger' 
            });
            setLoading(false);
            return;
        }

        if (netAmount < MIN_NET_AMOUNT) {
            setMessage({ 
                text: `Le montant net doit être d'au moins ${MIN_NET_AMOUNT} USDT.`,
                type: 'danger' 
            });
            setLoading(false);
            return;
        }
        
        if (!destinationWallet) {
            setMessage({ 
                text: 'Veuillez sélectionner un portefeuille de destination.',
                type: 'danger' 
            });
            setLoading(false);
            return;
        }

        const trimmedAddress = walletAddress.trim();
        // 🔧 Validation TRC20 plus flexible
        if (!/^T[A-Za-z0-9]{33,34}$/.test(trimmedAddress)) {
            setMessage({ 
                text: 'Adresse TRC20 invalide. Format attendu : T suivi de 33-34 caractères alphanumériques.',
                type: 'danger' 
            });
            setLoading(false);
            return;
        }

        try {
            const body = {
                amount: requestedAmount,
                walletAddress: trimmedAddress,
                network: NETWORK_VALUE,
                destinationWallet: destinationWallet
            };
            
            // 🔧 Utilisation de l'api configurée
            const res = await api.post('/withdrawal', body);
            
            if (res.status === 202) {
                setMessage({ 
                    text: '✅ Demande de retrait soumise avec succès ! En attente de validation administrative.',
                    type: 'success' 
                });
                setFormData({ amount: '', walletAddress: '', destinationWallet: '' });
                
                // 🔧 Recharger le solde
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
            
        } catch (err) {
            console.error('Erreur retrait:', err);
            
            let errorMsg = 'Erreur lors du traitement du retrait.';
            if (err.response) {
                const { status, data } = err.response;
                
                switch (status) {
                    case 400:
                        errorMsg = data.message || 'Données invalides.';
                        break;
                    case 401:
                        errorMsg = 'Session expirée. Veuillez vous reconnecter.';
                        setTimeout(() => navigate('/login'), 2000);
                        break;
                    case 403:
                        errorMsg = data.message || 'Accès refusé.';
                        break;
                    case 404:
                        errorMsg = 'Service de retrait non disponible.';
                        break;
                    case 429:
                        errorMsg = 'Trop de tentatives. Veuillez patienter.';
                        break;
                    case 500:
                        errorMsg = 'Erreur serveur. Veuillez réessayer plus tard.';
                        break;
                    default:
                        errorMsg = data.message || `Erreur ${status}`;
                }
            } else if (err.request) {
                errorMsg = 'Pas de réponse du serveur. Vérifiez votre connexion.';
            }
            
            setMessage({ 
                text: errorMsg,
                type: 'danger' 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="text-primary mb-0">💰 Retrait USDT</h1>
                
                {/* 🔧 Affichage solde */}
                {!loadingBalance && userBalance !== null && (
                    <div className="alert alert-info py-2 mb-0">
                        <strong>Solde disponible :</strong> {userBalance.toFixed(2)} USDT
                    </div>
                )}
            </div>

            <div className="alert alert-danger mb-4 text-center fw-bold">
                ⚠️ Réseau {NETWORK_NAME} uniquement. Adresse TRC20 obligatoire.
            </div>

            {/* 🔥 Message d'état */}
            {message.text && (
                <div className={`alert alert-${message.type} alert-dismissible fade show mb-4`} role="alert">
                    {message.text}
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setMessage({ text: '', type: '' })}
                        aria-label="Fermer"
                    ></button>
                </div>
            )}
            
            <div className="card shadow">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">📝 Formulaire de Retrait</h5>
                </div>
                <div className="card-body">
                    <form onSubmit={onSubmit}>
                        
                        <div className="form-group mb-4">
                            <label htmlFor="destinationWallet" className="form-label fw-bold">
                                Portefeuille de destination
                            </label>
                            <select
                                name="destinationWallet"
                                id="destinationWallet"
                                value={destinationWallet}
                                onChange={onChange}
                                required
                                className="form-select form-select-lg"
                                disabled={loading}
                            >
                                <option value="">-- Sélectionnez un portefeuille --</option>
                                {WALLET_OPTIONS.map((wallet, index) => (
                                    <option key={index} value={wallet}>{wallet}</option>
                                ))}
                            </select>
                            <div className="form-text">
                                Sélectionnez la plateforme où vous souhaitez recevoir vos USDT.
                            </div>
                        </div>
                        
                        <div className="form-group mb-4">
                            <label htmlFor="amount" className="form-label fw-bold">
                                Montant à retirer (USDT)
                            </label>
                            <div className="input-group">
                                <span className="input-group-text bg-light">$</span>
                                <input
                                    type="number"
                                    className="form-control form-control-lg"
                                    id="amount"
                                    placeholder={`Minimum: ${(NETWORK_FEE + MIN_NET_AMOUNT).toFixed(2)} USDT`}
                                    name="amount"
                                    value={amount}
                                    onChange={onChange}
                                    min={NETWORK_FEE + MIN_NET_AMOUNT}
                                    step="0.01"
                                    required
                                    disabled={loading}
                                />
                            </div>
                            <div className="form-text">
                                {userBalance !== null && (
                                    <span className="me-3">
                                        Solde disponible: <strong>{userBalance.toFixed(2)} USDT</strong>
                                    </span>
                                )}
                                <span>
                                    Minimum: <strong>{(NETWORK_FEE + MIN_NET_AMOUNT).toFixed(2)} USDT</strong>
                                </span>
                            </div>
                        </div>

                        <div className="form-group mb-4">
                            <label htmlFor="walletAddress" className="form-label fw-bold">
                                Adresse {NETWORK_NAME}
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-lg"
                                id="walletAddress"
                                placeholder={`Ex: TAbc123... (${NETWORK_VALUE} seulement)`}
                                name="walletAddress"
                                value={walletAddress}
                                onChange={onChange}
                                required
                                disabled={loading || !destinationWallet}
                            />
                            <div className="form-text">
                                <div className="d-flex align-items-center">
                                    <span className="me-2">⚠️</span>
                                    <span>Assurez-vous que c'est une adresse {NETWORK_VALUE} valide</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* 🔥 Résumé du retrait */}
                        {requestedAmount > 0 && (
                            <div className="summary-container mb-4">
                                <div className="border p-4 rounded bg-light shadow-sm">
                                    <h5 className="text-primary mb-3 border-bottom pb-2">
                                        <i className="fas fa-calculator me-2"></i>
                                        Récapitulatif du Retrait
                                    </h5>
                                    
                                    <div className="row mb-2">
                                        <div className="col-6 text-muted">Montant demandé :</div>
                                        <div className="col-6 text-end fw-bold">{requestedAmount.toFixed(2)} USDT</div>
                                    </div>
                                    
                                    <div className="row mb-1">
                                        <div className="col-6 text-muted">Frais de service (5%) :</div>
                                        <div className="col-6 text-end text-danger">- {serviceFee.toFixed(2)} USDT</div>
                                    </div>
                                    
                                    <div className="row mb-1">
                                        <div className="col-6 text-muted">Frais réseau :</div>
                                        <div className="col-6 text-end text-danger">- {networkFee.toFixed(2)} USDT</div>
                                    </div>
                                    
                                    <hr className="my-3"/>
                                    
                                    <div className="row mb-2">
                                        <div className="col-6 fw-bold">Total des frais :</div>
                                        <div className="col-6 text-end text-danger fw-bold">- {totalFees.toFixed(2)} USDT</div>
                                    </div>
                                    
                                    <div className="row mt-3 pt-3 border-top">
                                        <div className="col-6">
                                            <span className="text-success fw-bold h5">Montant net :</span>
                                        </div>
                                        <div className="col-6 text-end">
                                            <span className="fw-bold h4 text-success">{netAmount.toFixed(2)} USDT</span>
                                        </div>
                                    </div>
                                    
                                    <div className="alert alert-warning mt-3 mb-0">
                                        <small>
                                            <i className="fas fa-info-circle me-2"></i>
                                            Après validation administrative, vous recevrez <strong>{netAmount.toFixed(2)} USDT</strong> sur votre {destinationWallet}.
                                        </small>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="d-grid gap-2">
                            <button 
                                type="submit" 
                                className="btn btn-primary btn-lg"
                                disabled={loading || 
                                        requestedAmount <= 0 || 
                                        !walletAddress || 
                                        !destinationWallet ||
                                        netAmount < MIN_NET_AMOUNT ||
                                        (userBalance !== null && requestedAmount > userBalance)}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Traitement en cours...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane me-2"></i>
                                        Soumettre la demande de retrait
                                    </>
                                )}
                            </button>
                            
                            <button 
                                type="button" 
                                className="btn btn-outline-secondary btn-lg"
                                onClick={() => navigate('/dashboard')}
                                disabled={loading}
                            >
                                <i className="fas fa-arrow-left me-2"></i>
                                Retour au tableau de bord
                            </button>
                        </div>
                        
                    </form>
                </div>
                
                <div className="card-footer bg-light">
                    <small className="text-muted">
                        <i className="fas fa-shield-alt me-2"></i>
                        Les retraits sont soumis à validation manuelle par notre équipe. 
                        Le traitement peut prendre jusqu'à 24 heures ouvrées.
                    </small>
                </div>
            </div>
            
            {/* 🔧 Section informations */}
            <div className="row mt-4">
                <div className="col-md-6 mb-3">
                    <div className="card h-100 border-primary">
                        <div className="card-body">
                            <h5 className="card-title text-primary">
                                <i className="fas fa-info-circle me-2"></i>
                                Informations importantes
                            </h5>
                            <ul className="list-unstyled">
                                <li className="mb-2">✓ Réseau TRC20 uniquement</li>
                                <li className="mb-2">✓ Frais fixes : 1 USDT réseau + 5% service</li>
                                <li className="mb-2">✓ Validation administrative requise</li>
                                <li>✓ Temps de traitement : 24h maximum</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-6 mb-3">
                    <div className="card h-100 border-warning">
                        <div className="card-body">
                            <h5 className="card-title text-warning">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Sécurité
                            </h5>
                            <ul className="list-unstyled">
                                <li className="mb-2">🔒 Vérifiez deux fois votre adresse</li>
                                <li className="mb-2">🔒 Ne partagez jamais vos clés privées</li>
                                <li className="mb-2">🔒 BLACKROQ ne vous demandera jamais vos mots de passe</li>
                                <li>🔒 En cas de doute, contactez le support</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Withdrawal;